import React, { useState } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent, Chip,
  List, ListItem, ListItemText, ListItemIcon, Divider,
  Button, IconButton, Badge, LinearProgress, Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarTodayIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
  },
});

// Mock data for demonstration
const mockData = {
  totalEmployees: 254,
  activeEmployees: 238,
  pendingLeaveRequests: 12,
  newJoinersThisMonth: 8,
  upcomingExits: 5,
  payrollProcessed: 85,
  attendanceToday: 92,
  ongoingAppraisals: 17,
  trainingCompletionRate: 78,
};

const KPITile = ({ title, value, subtitle, icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', position: 'relative' }}>
    <CardContent>
      <Typography color="textSecondary" gutterBottom variant="overline">
        {title}
      </Typography>
      <Typography variant="h4" component="div">
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      )}
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, color }}>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const DashboardSection = ({ title, children, actionButton }) => (
  <Paper sx={{ p: 2, mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" component="h2">
        {title}
      </Typography>
      {actionButton}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const HRAdminDashboard = () => (
  <Box>
    {/* Quick Stats / KPI Tiles */}
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="Total Employees"
          value={mockData.totalEmployees}
          subtitle={`${mockData.activeEmployees} active`}
          icon={<PeopleIcon />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="Pending Leave Requests"
          value={mockData.pendingLeaveRequests}
          icon={<EventIcon color="warning" />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="New Joiners"
          value={mockData.newJoinersThisMonth}
          icon={<AccountCircleIcon color="success" />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="Upcoming Exits"
          value={mockData.upcomingExits}
          icon={<WarningIcon color="error" />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="Payroll Status"
          value={`${mockData.payrollProcessed}%`}
          subtitle="processed"
          icon={<AttachMoneyIcon color="primary" />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KPITile
          title="Attendance"
          value={`${mockData.attendanceToday}%`}
          subtitle="present today"
          icon={<CheckCircleIcon color="success" />}
        />
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      {/* Employee Management Widget */}
      <Grid item xs={12} md={6}>
        <DashboardSection 
          title="Employee Management"
          actionButton={<Button startIcon={<AddIcon />}>Add Employee</Button>}
        >
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" size="small" sx={{ mr: 1 }}>All Departments</Button>
            <Button variant="outlined" size="small" sx={{ mr: 1 }}>Active</Button>
            <Button variant="outlined" size="small">Location</Button>
          </Box>
          <List dense>
            <ListItem secondaryAction={<Chip label="3 missing" size="small" color="error" />}>
              <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
              <ListItemText primary="John Smith" secondary="IT Department" />
            </ListItem>
            <ListItem secondaryAction={<Chip label="Complete" size="small" color="success" />}>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText primary="Sarah Johnson" secondary="HR Department" />
            </ListItem>
          </List>
        </DashboardSection>
      </Grid>

      {/* Leave & Attendance Overview */}
      <Grid item xs={12} md={6}>
        <DashboardSection title="Leave & Attendance">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2">Pending Approvals: {mockData.pendingLeaveRequests}</Typography>
            <Button size="small">View Calendar</Button>
          </Box>
          <List dense>
            <ListItem secondaryAction={
              <Box>
                <Button size="small" color="success">Approve</Button>
                <Button size="small" color="error">Reject</Button>
              </Box>
            }>
              <ListItemIcon><CalendarTodayIcon /></ListItemIcon>
              <ListItemText 
                primary="Michael Brown" 
                secondary="Sick Leave - Dec 15-17, 2023" 
              />
            </ListItem>
          </List>
        </DashboardSection>
      </Grid>

      {/* Additional sections would be implemented similarly */}
    </Grid>
  </Box>
);

// Additional dashboard components for other roles would follow similar patterns
const SuperAdminDashboard = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Super Admin Dashboard</Typography>
    {/* Similar structure to HRAdminDashboard with different KPIs and sections */}
  </Box>
);

export const HREexecutiveDashboard = () => (
  <Box>
    <Typography variant="h4" gutterBottom>HR Executive Dashboard</Typography>
    {/* Similar structure with different KPIs and sections */}
  </Box>
);

const DepartmentManagerDashboard = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Department Manager Dashboard</Typography>
    {/* Similar structure with department-specific data */}
  </Box>
);

const TrainingDevelopmentDashboard = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Training & Development Dashboard</Typography>
    {/* Similar structure with training-specific data */}
  </Box>
);

const HRMSDashboard = () => {
  const [currentRole, setCurrentRole] = useState('hrAdmin');

  const renderDashboard = () => {
    switch(currentRole) {
      case 'superAdmin': return <SuperAdminDashboard />;
      case 'hrExecutive': return <HREexecutiveDashboard />;
      case 'deptManager': return <DepartmentManagerDashboard />;
      case 'training': return <TrainingDevelopmentDashboard />;
      default: return <HRAdminDashboard />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            HRMS Dashboard
          </Typography>
          <Box>
            <Chip 
              icon={<DashboardIcon />} 
              label="HR Admin" 
              color={currentRole === 'hrAdmin' ? 'primary' : 'default'} 
              onClick={() => setCurrentRole('hrAdmin')}
              sx={{ mr: 1 }}
            />
            <Chip 
              icon={<SettingsIcon />} 
              label="Super Admin" 
              color={currentRole === 'superAdmin' ? 'primary' : 'default'} 
              onClick={() => setCurrentRole('superAdmin')}
              sx={{ mr: 1 }}
            />
            {/* More role chips */}
          </Box>
        </Box>
        
        {renderDashboard()}
      </Box>
    </ThemeProvider>
  );
};

export default HRMSDashboard;